/**
 * FoundationsListPage – list of sound lessons (vowels / ñ / rr / j / silent h).
 * Shown after the Newcomer flashcards are mastered, or any time the learner
 * visits /foundations.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CaretLeft, CaretRight, CheckCircle, Waveform, SpinnerGap } from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { renderSafeSvg } from '@/lib/safeSvg';

export default function FoundationsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/foundations/lessons');
        setItems(data.items || []);
        setSummary({ total: data.total || 0, completed: data.completed || 0 });
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const flashcardsDone = !!user?.newcomer_flashcards_completed;
  const pct = summary.total
    ? Math.round((summary.completed / summary.total) * 100)
    : 0;

  return (
    <div className="lf-shell flex flex-col px-6 py-8" data-testid="foundations-list-page">
      <div className="flex items-center gap-2">
        <Link
          to={user?.level_kind === 'newcomer' ? '/home' : '/home'}
          className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
          data-testid="foundations-back"
        >
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            Newcomer · Sound Foundations
          </div>
          <h1 className="text-2xl font-bold">Get the sounds right</h1>
        </div>
      </div>

      {!flashcardsDone ? (
        <div className="mt-5 rounded-2xl border border-[var(--lf-accent)] bg-[#FFF6DB] px-4 py-3 text-sm" data-testid="foundations-unlock-hint">
          <div className="font-semibold">Finish your flashcards first</div>
          <div className="text-[var(--lf-text-muted)]">
            The sound lessons work best once you know a handful of words to practise on.
          </div>
          <button
            onClick={() => navigate('/newcomer')}
            className="lf-btn lf-btn-primary mt-3 py-2 text-sm"
            data-testid="foundations-go-flashcards"
          >
            Back to flashcards
          </button>
        </div>
      ) : null}

      {/* Progress strip */}
      <div className="mt-5 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3" data-testid="foundations-progress">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
          <span>Lessons completed</span>
          <span>{summary.completed} / {summary.total}</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--lf-surface-muted)]">
          <motion.div
            className="h-full bg-[var(--lf-primary)]"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {error ? <div className="mt-3 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-[var(--lf-text-muted)]">
          <SpinnerGap className="animate-spin" size={18} /> Loading lessons…
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="foundations-lesson-list">
          {items.map((les, i) => (
            <motion.button
              key={les.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => navigate(`/foundations/${les.id}`)}
              className={`lf-card flex w-full items-center gap-4 p-4 text-left transition-all hover:-translate-y-0.5 ${
                les.progress?.completed ? 'bg-gradient-to-br from-[#FFFFFF] to-[#FFF5EE]' : ''
              }`}
              data-testid={`foundations-lesson-${les.id}`}
            >
              <div
                className="grid h-16 w-16 flex-shrink-0 place-items-center rounded-2xl bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]"
                aria-hidden
                {...renderSafeSvg(les.mouth_svg)}
              />
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
                    Lesson {les.order}
                  </div>
                  <div className="text-xs font-mono text-[var(--lf-text-muted)]">{les.ipa}</div>
                </div>
                <div className="mt-0.5 font-bold">{les.title}</div>
                <div className="text-xs text-[var(--lf-text-muted)] line-clamp-1">{les.summary}</div>
              </div>
              {les.progress?.completed ? (
                <CheckCircle size={24} weight="fill" className="text-[var(--lf-primary)]" />
              ) : (
                <CaretRight size={20} className="text-[var(--lf-text-muted)]" />
              )}
            </motion.button>
          ))}
        </div>
      )}

      {!loading && summary.completed >= summary.total && summary.total > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lf-card mt-6 p-5 bg-gradient-to-br from-[#FFFFFF] to-[#F7EFE9]"
          data-testid="foundations-complete-card"
        >
          <Waveform size={24} weight="duotone" className="text-[var(--lf-primary)]" />
          <div className="mt-2 text-xl font-bold">All sounds unlocked!</div>
          <div className="text-sm text-[var(--lf-text-muted)]">
            You're ready for your first conversation — tap below to start.
          </div>
          <button
            onClick={() => navigate('/home')}
            className="lf-btn lf-btn-primary mt-4"
            data-testid="foundations-finish-cta"
          >
            Go to Home <CaretRight size={16} weight="bold" />
          </button>
        </motion.div>
      ) : null}
    </div>
  );
}
